// Tyro iClient (headless) service wrapper.
//
// The SDK is served from one of three CDNs depending on environment:
//   test     → iclientsimulator.test.tyro.com/iclient-v1.js  (simulator, no physical terminal)
//   preprod  → iclient.test.tyro.com/iclient-v1.js           (real terminal, test money)
//   prod     → iclient.tyro.com/iclient-v1.js                (live money)
//
// To pair / drive the simulator from a browser, Tyro hosts a configuration page at
//   https://iclientsimulator.test.tyro.com/configuration.html  (test simulator)
//   https://iclient.test.tyro.com/configuration.html           (preprod, with terminal)
//   https://iclient.tyro.com/configuration.html                (production)
//
// We lazy-load the script only when the user actually tries to pair or
// transact — nothing is downloaded on pages that don't use Tyro.
//
// Usage:
//   const tyro = await initTyro({ env: "test", apiKey: "..." });
//   await tyro.pair({ mid, tid, onQuestion, onStatus });
//   await tyro.purchase({ amountCents, onStatus, onReceipt });

const CDN_BY_ENV = {
  // Simulator — no physical terminal needed. Pair via:
  //   https://iclientsimulator.test.tyro.com/configuration.html
  test:    "https://iclientsimulator.test.tyro.com/iclient-v1.js",
  // Pre-prod: real terminal on test money. Requires partner enablement.
  preprod: "https://iclient.test.tyro.com/iclient-v1.js",
  // Production: real cards. API key issued by Tyro ONLY after certification.
  prod:    "https://iclient.tyro.com/iclient-v1.js",
};

// Tyro's simulator doesn't require an API key at all — the constructor
// accepts any non-empty string in test env. We pass a placeholder so the
// SDK validation doesn't complain.
const PUBLIC_DEV_API_KEY = "iclient-simulator";

// --- Script loader (singleton per env) ----------------------------------
let _scriptPromise = null;
let _loadedEnv = null;

function loadScript(env) {
  if (_scriptPromise && _loadedEnv === env) return _scriptPromise;
  if (_loadedEnv && _loadedEnv !== env) {
    // Switching envs in the same tab requires a hard reload because Tyro
    // keeps the CDN URL as part of the singleton SDK state.
    return Promise.reject(new Error(
      `Tyro SDK already loaded for ${_loadedEnv}; reload the page to switch to ${env}.`
    ));
  }
  const src = CDN_BY_ENV[env];
  if (!src) return Promise.reject(new Error(`Unknown Tyro env: ${env}`));
  _scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (window.TYRO) { _loadedEnv = env; return resolve(window.TYRO); }
      // Orphaned <script> in the DOM but window.TYRO has been wiped
      // (resetTyroSdk used to delete it). The script already fired its
      // `load` event once and won't re-fire — attaching a listener here
      // would hang forever, which is exactly the cancel-then-retry bug
      // the user reported ("Connecting to terminal…" never moves).
      //
      // Cleanest recovery: remove the stale tag and inject a fresh one
      // so the new <script>'s `load` event fires normally.
      try { existing.remove(); } catch { /* noop */ }
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => {
      if (!window.TYRO) return reject(new Error("Tyro SDK loaded but window.TYRO missing"));
      _loadedEnv = env;
      resolve(window.TYRO);
    };
    s.onerror = () => reject(new Error("Tyro SDK failed to load (CDN unreachable)"));
    document.head.appendChild(s);
  });
  return _scriptPromise;
}

// --- Public factory -----------------------------------------------------
export async function initTyro({ env = "test", apiKey, posProduct = {} } = {}) {
  const TYRO = await loadScript(env);
  const key = (apiKey && apiKey.trim()) || PUBLIC_DEV_API_KEY;
  // Cert POSinfo.2: posProductVendor / posProductName / posProductVersion
  // MUST NOT contain spaces and MUST NOT be null/empty. Sanitise every
  // string we hand to the iClient so a store rename to "Tom's Repair Cafe"
  // can never silently fail the cert.
  const safe = (v, fallback) => {
    const s = (v ?? "").toString().trim().replace(/\s+/g, "");
    return s || fallback;
  };
  const client = new TYRO.IClient(key, {
    posProductVendor:   safe(posProduct.vendor,  "iRepairExperts"),
    posProductName:     safe(posProduct.name,    "iRepairPOS"),
    posProductVersion:  safe(posProduct.version, "1.0.0"),
    // Per Tyro docs: an opaque merchant-supplied string identifying the
    // physical location — used by Tyro support to trace issues across
    // multi-store deployments. We use the active store name. The cert
    // POSinfo.2 no-space rule only applies to the three posProduct*
    // fields above; siteReference is free-form text.
    siteReference:      (posProduct.siteReference || "default").toString().trim() || "default",
  });

  return {
    raw: client,

    /**
     * Pair the POS with a physical terminal. Resolves with the
     * `integration_key` returned by Tyro; call `onStatus` throughout for
     * live prompts ("Press YES on terminal to approve pairing…").
     */
    pair({ mid, tid, onStatus } = {}) {
      return new Promise((resolve, reject) => {
        // Per Tyro spec: responseReceivedCallback fires multiple times
        // during pairing with `{status, message, integrationKey}`.
        //   status="inProgress" → narrate in UI
        //   status="success"    → resolve with the returned integration_key
        //   status="failure"    → reject with the terminal's error message
        const handler = (res) => {
          if (res?.status === "inProgress") {
            onStatus?.(res.message || "Pairing in progress…");
            return;
          }
          if (res?.status === "success") {
            resolve({ integrationKey: res.integrationKey || "", raw: res });
          } else {
            reject(new Error(res?.message || "Pairing failed"));
          }
        };
        try {
          client.pairTerminal(String(mid), String(tid), handler);
        } catch (e) { reject(e); }
      });
    },

    /**
     * Take a payment. Accepts amountCents (integer). Per the Tyro spec we
     * pass MID/TID/integrationKey on EVERY call so the transaction doesn't
     * depend on browser localStorage — safer for multi-store POS.
     *
     * Resolves with a normalised object on APPROVED:
     *   { result, authorisationCode, cardType, transactionReference, rrn,
     *     elidedPan, transactionId, transactionAmount, baseAmount,
     *     customerReceipt, raw }
     *
     * Rejects for CANCELLED / DECLINED / SYSTEM ERROR / NOT STARTED.
     * Rejects with a special flag `unknown: true` on UNKNOWN (network/
     * hardware blip — merchant must check the physical terminal).
     */
    purchase({ amountCents, mid, tid, integrationKey, transactionId,
               cashoutCents, enableSurcharge = false,
               integratedReceipt = true, timeoutMs = 90000,
               onStatus, onQuestion, onReceipt } = {}) {
      const requestParams = {
        amount: String(Math.round(amountCents)),
        // Tyro spec REQUIRES this boolean. true = POS prints the cardholder
        // receipt (we render it inline on the tax invoice). false = the
        // terminal prints its own slip. Driven by per-store cert toggle.
        integratedReceipt: !!integratedReceipt,
        enableSurcharge: !!enableSurcharge,
      };
      if (cashoutCents && cashoutCents > 0) {
        requestParams.cashout = String(Math.round(cashoutCents));
      }
      // Per Tyro spec: MID/TID are merchant identifiers and must be strings.
      // Some SDK versions silently ignore numeric values, leading to "POS
      // not paired" errors that look identical to a stale integration_key.
      if (mid) requestParams.mid = String(mid);
      if (tid) requestParams.tid = String(tid);
      if (integrationKey) requestParams.integrationKey = integrationKey;
      if (transactionId)  requestParams.transactionId  = transactionId;
      return _runTransaction(client.initiatePurchase.bind(client), requestParams,
                             { onStatus, onQuestion, onReceipt, timeoutMs,
                               cancel: () => { try { client.cancelCurrentTransaction(); } catch { /* noop */ } } });
    },

    /**
     * Refund. Per spec:
     *   • No `enableSurcharge` — refunds cannot carry a surcharge flag.
     *   • No `cashout` on a refund.
     *   • `amount` should equal the FULL original purchase amount (i.e.
     *     inclusive of whatever surcharge Tyro applied on the original
     *     transaction) — staff-facing copy in the UI will surface this.
     */
    refund({ amountCents, mid, tid, integrationKey, transactionId,
             integratedReceipt = true, timeoutMs = 90000,
             onStatus, onQuestion, onReceipt } = {}) {
      const requestParams = {
        amount: String(Math.round(amountCents)),
        // Tyro spec REQUIRES this boolean — same semantics as purchase.
        integratedReceipt: !!integratedReceipt,
      };
      // Per Tyro spec: MID/TID must be strings.
      if (mid) requestParams.mid = String(mid);
      if (tid) requestParams.tid = String(tid);
      if (integrationKey) requestParams.integrationKey = integrationKey;
      if (transactionId)  requestParams.transactionId  = transactionId;
      return _runTransaction(client.initiateRefund.bind(client), requestParams,
                             { onStatus, onQuestion, onReceipt, timeoutMs,
                               cancel: () => { try { client.cancelCurrentTransaction(); } catch { /* noop */ } } });
    },

    cancel() {
      try { client.cancelCurrentTransaction(); } catch { /* noop */ }
    },

    /**
     * Integrated Reports — Tyro cert "Integrated Reports" HR'd item.
     *
     * Wraps `reconciliationReport(requestParams, callback)` from the iClient
     * SDK. Tyro accepts these requestParams:
     *   • terminalBusinessDay: true | false  (true = current day; false = previous)
     *   • type: "summary" | "detail"
     *   • format: "xml" | "txt"
     * Plus the usual {mid, tid, integrationKey} we send on every call.
     *
     * The SDK returns `{result, report}` where report is the raw XML or
     * plain-text reconciliation from the acquirer. We return both the raw
     * payload and a lightweight parsed summary so the Reports page can
     * display totals without parsing XML on the caller side.
     */
    reconciliationReport({ mid, tid, integrationKey,
                           currentDay = true, businessDay,
                           type = "detail", format = "txt",
                           timeoutMs = 60000 } = {}) {
      return new Promise((resolve, reject) => {
        // Per the bundled Tyro iClient API spec
        // (TYRO.IClient.reconciliationReport):
        //   • terminalBusinessDay: yyyyMMdd (e.g. "20261225") — NOT yymmdd
        //   • type: "summary" | "detail"
        //   • format: "txt" | "xml"
        // Response: { result: "success"|"failure", format, type, data, error }
        const formatYYYYMMDD = (d) => {
          const yyyy = String(d.getFullYear());
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          return `${yyyy}${mm}${dd}`;
        };
        let bd = businessDay;
        if (!bd) {
          const d = new Date();
          if (!currentDay) d.setDate(d.getDate() - 1);
          bd = formatYYYYMMDD(d);
        } else {
          // Normalise any caller-supplied date to the strict yyyymmdd
          // shape the iClient expects (no dashes). Accept either
          //   ISO "yyyy-mm-dd"
          //   yyyymmdd already-compact
          //   a `Date` instance
          // — anything else falls back to "today" so we never send the
          // SDK a malformed date that it silently treats as today
          // (which was the previous bug: picking "Mon Feb 9" sent
          // "2026-02-09" to the SDK, the SDK couldn't parse it, and
          // it returned the CURRENT day's data instead of the past
          // day, exactly what the cert reviewer would flag).
          if (bd instanceof Date) {
            bd = formatYYYYMMDD(bd);
          } else if (typeof bd === "string") {
            // strip everything that isn't a digit
            const digits = bd.replace(/\D/g, "");
            if (digits.length === 8) {
              bd = digits;
            } else {
              // unrecognised shape — fall back to today so we at least
              // get a valid response (and log a console warning so
              // any future caller can see what happened).
              console.warn("[tyro-recon] unrecognised businessDay format, falling back to today:", businessDay);
              bd = formatYYYYMMDD(new Date());
            }
          } else {
            bd = formatYYYYMMDD(new Date());
          }
        }
        const tyroType = type === "detailed" ? "detail" : type;
        const tyroFormat = format === "text" ? "txt" : format;
        const requestParams = {
          terminalBusinessDay: bd,
          type: tyroType,
          format: tyroFormat,
        };
        if (mid) requestParams.mid = String(mid);
        if (tid) requestParams.tid = String(tid);
        if (integrationKey) requestParams.integrationKey = integrationKey;
        const timer = setTimeout(() => {
          reject(Object.assign(new Error("Reconciliation timed out"), { timeout: true }));
        }, timeoutMs);
        try {
          console.log("[tyro-recon] requestParams:", requestParams);
          // Tyro spec: reconciliationReport(requestParams, responseReceivedCallback)
          client.reconciliationReport(requestParams, (res) => {
            clearTimeout(timer);
            console.log("[tyro-recon] response:", res);
            if (res?.result === "success") {
              // Tyro iClient firmware revisions deliver the report body
              // under different keys depending on year / acquirer build:
              //   • current spec: res.report  (string)
              //   • some 2024 builds: res.data    (string)
              //   • xml format ones: res.xml     (string)
              //   • text format ones: res.text    (string)
              //   • some builds nest: res.report.text  /  res.report.data
              // Pick the first non-empty string we can find so the card
              // always renders content instead of "(empty report)".
              const pickReport = (r) => {
                if (!r) return "";
                if (typeof r.report === "string" && r.report) return r.report;
                if (typeof r.data   === "string" && r.data)   return r.data;
                if (typeof r.text   === "string" && r.text)   return r.text;
                if (typeof r.xml    === "string" && r.xml)    return r.xml;
                if (r.report && typeof r.report === "object") {
                  return r.report.text || r.report.data || r.report.xml || "";
                }
                return "";
              };
              const body = pickReport(res);
              if (!body) {
                console.warn("[tyro-recon] success but no body in response keys:", Object.keys(res || {}));
              }
              resolve({
                ok: true,
                raw: body,
                format: res.format || tyroFormat,
                type: res.type || tyroType,
                businessDay: bd,
                currentDay,
                receivedAt: new Date().toISOString(),
                _rawSdkResponse: res,
              });
            } else {
              reject(Object.assign(new Error(res?.error || "Reconciliation failed"),
                { result: res?.result, error: res?.error, raw: res }));
            }
          });
        } catch (e) { clearTimeout(timer); reject(e); }
      });
    },

    /**
     * Health check — calls `terminalInfo` which returns the terminal's status,
     * serial, software version, and whether any transactions are pending.
     * Used by the Settings "Test connection" button and the Reports page
     * "Terminal status" block. Shared between both per Tyro's spec — there
     * is no separate healthCheck method, terminalInfo is the canonical ping.
     */
    terminalInfo({ mid, tid, integrationKey, omitParams = false, timeoutMs = 20000 } = {}) {
      return new Promise((resolve, reject) => {
        // Per the official Tyro iClient API spec (TYRO.IClient.terminalInfo):
        //   responseReceivedCallback is called with { status, message,
        //   terminalInfo: { name, version, available,
        //                    currentTerminalBusinessDay,
        //                    nextAutoSettlementTime } }
        //   - status === "inProgress" → more responses will follow
        //   - status === "success"    → terminalInfo populated
        //   - status === "failure"    → message describes the error
        const requestParams = {};
        if (!omitParams) {
          if (mid) requestParams.mid = String(mid);
          if (tid) requestParams.tid = String(tid);
          if (integrationKey) requestParams.integrationKey = integrationKey;
        }
        const timer = setTimeout(() => {
          reject(Object.assign(new Error("Terminal info timed out"), { timeout: true }));
        }, timeoutMs);
        try {
          // Tyro spec: terminalInfo(responseReceivedCallback, requestParams)
          client.terminalInfo((res) => {
            console.log("[tyro-terminalInfo] callback:", res, "omitParams:", omitParams);
            // Ignore in-progress notifications — they're status updates,
            // the SDK will call us again when the actual response arrives.
            if (res?.status === "inProgress") return;
            clearTimeout(timer);
            if (res?.status === "success") {
              const ti = res.terminalInfo || {};
              resolve({
                ok: true,
                status: "success",
                name: ti.name || "",
                version: ti.version || "",
                available: !!ti.available,
                currentTerminalBusinessDay: ti.currentTerminalBusinessDay || "",
                nextAutoSettlementTime: ti.nextAutoSettlementTime || "",
                raw: res,
              });
            } else {
              reject(Object.assign(new Error(res?.message || "Terminal unreachable"),
                { status: res?.status, raw: res }));
            }
          }, requestParams);
        } catch (e) { clearTimeout(timer); reject(e); }
      });
    },
  };
}

/**
 * Hard reset — nukes any orphaned `iclient-headless-iframe` from the DOM and
 * clears module-level singletons. Use this when a transaction hangs and the
 * SDK starts throwing "Transaction already in progress" or silently
 * ignoring new initiatePurchase() calls. Forces a fresh SDK load on the
 * next initTyro() call.
 */
export function resetTyroSdk() {
  try {
    document.querySelectorAll('iframe#iclient-headless-iframe').forEach((f) => f.remove());
  } catch { /* noop */ }
  // Drop the cached script promise so the next initTyro re-loads cleanly.
  _scriptPromise = null;
  _loadedEnv = null;
  // NOTE: we INTENTIONALLY do NOT `delete window.TYRO` here.
  //
  // Background: previously we did, but on cancel-then-retry the SDK
  // script <tag> in the DOM had already fired its `load` event, so the
  // next loadScript() would attach a fresh `load` listener that NEVER
  // fired — wedging the dialog at "Connecting to terminal…".
  //
  // Keeping window.TYRO intact lets the next initTyro() reuse the
  // already-downloaded SDK module to construct a brand-new IClient
  // instance (cheap; just a class instantiation). The orphan iframe
  // is gone, the `_scriptPromise` cache is dropped — that's enough
  // to give us a clean slate for the next transaction without forcing
  // a full network round-trip to re-download the SDK.
  // (loadScript() also has a safety net that re-injects the script
  //  if it discovers an orphan tag + missing window.TYRO, so even
  //  callers who do delete window.TYRO externally won't get stuck.)
}

// Internal alias — used by _runTransaction's force-reject path. Split out
// so the timeout code inside _runTransaction can call it without forward
// reference issues.
function _forceReset() { resetTyroSdk(); }

// ------------------------------------------------------------------------
// Shared transaction runner — identical shape for purchase AND refund, per
// Tyro spec. Encapsulates the 4 callbacks and translates the
// `transactionCompleteCallback` result strings into a Promise that the
// calling UI can simply await.
// ------------------------------------------------------------------------
function _runTransaction(sdkFn, requestParams, { onStatus, onQuestion, onReceipt,
                                                 timeoutMs = 90000, cancel } = {}) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timer = null;
    let forceTimer = null;
    // Latched signature flag — Tyro's iClient delivers `signatureRequired`
    // on the receiptCallback (per official iClient API spec), NOT on the
    // final transactionCompleteCallback. Older code only checked the final
    // callback so signed sales like $100.02 / $100.08 came back with the
    // flag missing. We capture the flag from the receipt callback into a
    // closure variable and OR it into the normalised result before
    // resolving the promise.
    let sigRequiredLatched = false;
    // Latched DCC fields — Tyro's iClient delivers DCC data on the
    // receiptCallback or transactionCompleteCallback depending on
    // firmware. Field names vary slightly (cardCurrency vs dccCurrency,
    // exchangeRate vs dccRate). We latch the union and let the consumer
    // parse from a single normalised object.
    let dccLatched = null;
    const _absorbDcc = (src) => {
      if (!src) return;
      const ccy = src.cardCurrency || src.dccCurrency || src.transactionCurrency || src.foreignCurrency || "";
      const baseCcy = src.baseCurrency || "AUD";
      // DCC only applies if the customer paid in a currency OTHER than the
      // merchant's base currency. Domestic cards return baseCurrency only.
      if (!ccy || String(ccy).toUpperCase() === String(baseCcy).toUpperCase()) return;
      const rate = src.exchangeRate || src.dccRate || src.dccExchangeRate || "";
      const foreign = src.dccAmount || src.foreignAmount || (
        // If the response carries transactionAmount in the foreign currency
        // (some firmware returns this on DCC), grab it.
        ccy !== "AUD" && src.transactionAmount ? src.transactionAmount : ""
      );
      const markup = src.markup || src.dccMarkup || src.dccMarkupPct || src.commission || 5.0;
      dccLatched = {
        applied: true,
        currency: String(ccy).toUpperCase(),
        amount: parseFloat(foreign) || 0,
        exchange_rate: rate ? String(rate) : "",
        markup_pct: parseFloat(markup) || 5.0,
        provider: src.dccProvider || "Pure Commerce",
        customer_receipt: src.customerReceipt || "",
      };
    };
    const finish = (fn, val) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      if (forceTimer) clearTimeout(forceTimer);
      fn(val);
    };

    // Hard timeout — if the SDK never resolves (orphaned iframe, busy
    // simulator, network glitch) we don't want the cert harness button stuck
    // on "Running…" forever. Default 90s comfortably exceeds normal Tyro
    // approve / decline / signature flows, but bails out before hanging the UI.
    timer = setTimeout(() => {
      // First try the polite cancel — asks Tyro's SDK to cleanly tear down.
      try { cancel?.(); } catch { /* noop */ }
      // If the polite cancel succeeded, a callback will arrive within ~2s
      // and resolve/reject the Promise normally. If it doesn't — because
      // the iframe is fully wedged or postMessage is partitioned — we
      // force-reject on a secondary timer AND hard-reset the SDK so the
      // orphaned iframe can't poison the next transaction. This is the
      // fix for the "Running… 113s" bug where the timeout fired but the
      // Promise never settled because the SDK ate the cancel().
      forceTimer = setTimeout(() => {
        try {
          // eslint-disable-next-line no-use-before-define
          _forceReset();
        } catch { /* noop */ }
        finish(reject, Object.assign(new Error("Transaction timed out"), {
          timeout: true, result: "TIMEOUT",
        }));
      }, 2000);
    }, timeoutMs);

    // Tyro iClient expects TWO arguments: (requestParams, transactionCallbacks).
    // Callbacks live on the SECOND object — confirmed against iclient-v1.js
    // source which expects `transactionCallbacks.questionCallback`, etc.
    const transactionCallbacks = {
      statusMessageCallback: (m) => {
        const text = typeof m === "string" ? m : (m?.message || "");
        onStatus?.(text);
      },

      // Spec confirmed via SDK source:
      //   posQuestionChangedCallback(param, ttaAnswerCallback)
      // i.e. questionCallback is invoked with (question, answerCallback) —
      // two positional args. We expose them to our consumer as a flat
      // `{text, options, answer}` triple to keep dialogs idiomatic in React.
      questionCallback: (question, answerCallback) => {
        // Diagnostic — confirms what the simulator is asking and whether
        // our signature-detection branch fires.
        try { console.log("[tyro] questionCallback prompt:", question?.text, "options:", question?.options); } catch { /* noop */ }
        // ── Signature-required detection ──────────────────────────────
        // Tyro's hosted SIMULATOR diverges from the official iClient spec:
        // for some firmware/magic-amount combinations it never sets
        // `r.signatureRequired` on the receiptCallback. The ONLY reliable
        // signal that a signature flow happened is this `questionCallback`
        // firing with prompt text containing "signature" (e.g. "Signature
        // OK?"). Latch it here so the resolved promise carries the flag
        // even when the receipt + final callbacks both omit it.
        const qText = (question?.text || "").toLowerCase();
        if (qText.includes("signature")) {
          sigRequiredLatched = true;
        }
        if (!onQuestion) {
          // Default: pick the first option so the terminal isn't stuck.
          try { answerCallback(question?.options?.[0]); } catch { /* noop */ }
          return;
        }
        onQuestion({
          text: question?.text || "",
          options: Array.isArray(question?.options) ? question.options : [],
          answer: (chosen) => {
            try { answerCallback(chosen); } catch { /* noop */ }
          },
        });
      },

      receiptCallback: (r) => {
        if (!r) return;
        // Diagnostic: log the full payload so we can confirm whether the
        // simulator is delivering signatureRequired (or never sends it)
        // for this firmware.
        try { console.log("[tyro] receiptCallback payload:", r); } catch { /* noop */ }
        // Latch — Tyro spec says signatureRequired arrives on the
        // receiptCallback, never the final callback. We OR it into the
        // closure so the resolved promise can include it.
        if (r.signatureRequired) sigRequiredLatched = true;
        // Latch DCC fields — they may arrive on the receipt callback for
        // some firmware/scheme combos.
        _absorbDcc(r);
        onReceipt?.({
          merchantReceipt: r.merchantReceipt || "",
          signatureRequired: !!r.signatureRequired,
          kind: "merchant",
        });
      },

      transactionCompleteCallback: (final) => {
        const result = (final?.result || "").toUpperCase();
        // Tyro iClient v1 returns `baseAmount` / `transactionAmount` as
        // STRING DOLLAR values (e.g. "104.55"), NOT cents. Earlier code
        // divided by 100 thinking they were cents, which silently scaled
        // every receipt by 100x ($102.50 became $1.0455 in the DB).
        // Also: `surchargeAmount` is its OWN top-level field on the
        // response — read it directly instead of subtracting (the
        // simulator sometimes returns base==transaction when surcharge
        // applies, which makes subtraction return 0).
        const numAmount = (v) => {
          const n = parseFloat(v);
          return Number.isFinite(n) ? n : 0;
        };
        const normalised = {
          result,
          authorisationCode: final?.authorisationCode || "",
          cardType: final?.cardType || "",
          transactionReference: final?.transactionReference || "",
          rrn: final?.rrn || "",
          elidedPan: final?.elidedPan || "",
          transactionId: final?.transactionId || "",
          transactionAmount: numAmount(final?.transactionAmount),
          baseAmount:        numAmount(final?.baseAmount),
          surchargeAmount:   numAmount(final?.surchargeAmount),
          tipAmount:         numAmount(final?.tipAmount),
          // signatureRequired comes from receiptCallback per Tyro spec —
          // the final callback may omit it. OR with the closure-latched
          // value so signed sales aren't silently lost.
          signatureRequired: !!final?.signatureRequired || sigRequiredLatched,
          customerReceipt: final?.customerReceipt || "",
          dcc: (function () {
            // DCC may also arrive on the final callback — absorb again
            // before snapshotting the latch.
            _absorbDcc(final);
            return dccLatched;  // null when no DCC applied
          })(),
          raw: final,
        };
        if (normalised.customerReceipt) {
          onReceipt?.({
            merchantReceipt: "",
            customerReceipt: normalised.customerReceipt,
            signatureRequired: normalised.signatureRequired,
            kind: "customer",
          });
        }
        if (result === "APPROVED") return finish(resolve, normalised);
        if (result === "UNKNOWN") {
          return finish(reject, Object.assign(new Error("Transaction status unknown — check the terminal"), {
            unknown: true, ...normalised,
          }));
        }
        finish(reject, Object.assign(new Error(`Transaction ${result || "failed"}`), normalised));
      },
    };

    try {
      sdkFn(requestParams, transactionCallbacks);   // <-- two-arg call per SDK source
    } catch (e) { finish(reject, e); }
  });
}

export const TYRO_ENVS = [
  { value: "test",    label: "Sandbox (simulator)", hint: "No physical terminal needed" },
  { value: "preprod", label: "Pre-prod (real terminal, test money)", hint: "Use only for certification" },
  { value: "prod",    label: "Production (live money)", hint: "Real customers, real cards" },
];
